import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { langFromPath, stripLangPrefix, localizePath, URL_LANGS } from "@/i18n/routing";

const SITE_URL = "https://barefootluxury.be";

const LOCALE_MAP = {
    en: "en_GB",
    fr: "fr_FR",
    nl: "nl_BE",
    pt: "pt_PT",
};

function setMeta(selector, value) {
    if (!value) return;
    const el = document.head.querySelector(selector);
    if (el) el.setAttribute("content", value);
}

/**
 * Updates document.title, <html lang>, canonical, hreflang alternates,
 * description and OpenGraph/Twitter meta based on the current i18n language,
 * the URL language prefix and the given translation key (e.g. "home", "press").
 * Pass options.noindex=true on 404/error pages so Google won't index them.
 */
export default function useSEO(page, options) {
    const { t, i18n } = useTranslation();
    const { pathname } = useLocation();
    const noindex = !!(options && options.noindex);

    useEffect(() => {
        const lang = (i18n.language || "en").slice(0, 2);
        const title = t(`seo.${page}.title`);
        const description = t(`seo.${page}.description`);
        const siteName = t("seo.siteName");
        const relImage = t("seo.defaultOgImage");
        const basePath = stripLangPrefix(pathname).replace(/\/+$/, "") || "/";
        const canonicalUrl = `${SITE_URL}${localizePath(basePath, langFromPath(pathname))}`;
        const image = relImage.startsWith("http") ? relImage : `${SITE_URL}${relImage}`;

        document.documentElement.lang = lang;
        if (title) document.title = title;

        // Robots meta: noindex,follow for 404-style pages; default is index,follow.
        let robots = document.head.querySelector('meta[name="robots"]');
        if (!robots) {
            robots = document.createElement("meta");
            robots.setAttribute("name", "robots");
            document.head.appendChild(robots);
        }
        robots.setAttribute("content", noindex ? "noindex, follow" : "index, follow");

        // Prerender / crawler status hint (some crawlers honour this on SPAs).
        let prerender = document.head.querySelector('meta[name="prerender-status-code"]');
        if (noindex) {
            if (!prerender) {
                prerender = document.createElement("meta");
                prerender.setAttribute("name", "prerender-status-code");
                document.head.appendChild(prerender);
            }
            prerender.setAttribute("content", "404");
        } else if (prerender) {
            prerender.remove();
        }

        let canonical = document.head.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement("link");
            canonical.setAttribute("rel", "canonical");
            document.head.appendChild(canonical);
        }
        canonical.setAttribute("href", canonicalUrl);

        document.head.querySelectorAll("link[data-hreflang]").forEach((el) => el.remove());

        // Do not emit hreflang alternates for noindex pages: they should not
        // participate in the multilingual cluster.
        if (!noindex) {
            const addAlternate = (hreflang, href) => {
                const link = document.createElement("link");
                link.setAttribute("rel", "alternate");
                link.setAttribute("hreflang", hreflang);
                link.setAttribute("href", href);
                link.setAttribute("data-hreflang", "");
                document.head.appendChild(link);
            };
            URL_LANGS.forEach((l) => addAlternate(l, `${SITE_URL}${localizePath(basePath, l)}`));
            addAlternate("x-default", `${SITE_URL}${basePath}`);
        }

        setMeta('meta[name="description"]', description);
        setMeta('meta[name="keywords"]', t("seo.keywords"));
        setMeta('meta[property="og:title"]', title);
        setMeta('meta[property="og:description"]', description);
        setMeta('meta[property="og:image"]', image);
        setMeta('meta[property="og:site_name"]', siteName);
        setMeta('meta[property="og:url"]', canonicalUrl);
        setMeta('meta[property="og:locale"]', LOCALE_MAP[lang] || "en_GB");

        setMeta('meta[name="twitter:title"]', title);
        setMeta('meta[name="twitter:description"]', description);
        setMeta('meta[name="twitter:image"]', image);
    }, [page, t, i18n.language, pathname, noindex]);
}
